import React, { useEffect, useId, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { makeDeposits, makeWithdrawals } from '@/api/custom';
import type { ThemeType } from '@/contexts/ThemeContext';
import { fundingBankInitials, fundingBankLogoSrc } from '@/util/bank-link';
import type { FundingChip, TransferBankChoice } from '@/util/bank-link';
import type { TransferMode } from './transfer-button-rules';
import {
  contributionTypes,
  contributionYears,
  distributionTypes,
  emptyIraInput,
  iraDepositFields,
  iraInputError,
  iraTypeLabel,
  iraWithdrawFields,
  withholdingOptions,
} from './ira-transfer';
import type { IraTransferInput, IraTransferRules } from './ira-transfer';

export type TransferCashTarget = {
  mode: TransferMode;
  accountId: string;
  accountName: string;
  accountTypeLabel: string;
  fundingChip?: FundingChip;
  bankLabel: string;
  bankDetail: string;
  bankLogo?: string | null;
  availableToWithdraw: number | null;
};

type TransferCashModalProps = {
  target: TransferCashTarget | null;
  onClose: () => void;
  onCompleted: (accountId: string) => void;
  /** Set for an IRA: adds the contribution or distribution fields Apex needs. */
  ira?: IraTransferRules | null;
  /**
   * The current available-to-withdraw, read live from the store: financial
   * details can land after the modal opens. Overrides the target's snapshot.
   */
  availableToWithdraw?: number | null;
  /** The refresh of that balance; review and confirm wait until it's 'done'. */
  balanceStatus?: 'loading' | 'failed' | 'done';
  onRetryBalance?: () => void;
  /**
   * Set for an attach-all wallet: the banks it can move money with, read live.
   * The bank side becomes a choice instead of the locked funding bank.
   */
  bankChoices?: TransferBankChoices;
  onRetryBanks?: () => void;
  theme?: ThemeType;
};

export type TransferBankChoices = {
  status: 'loading' | 'failed' | 'ready';
  choices: TransferBankChoice[];
};

// The shared formatCurrency drops cents; a transfer must show the exact amount sent.
const formatTransferAmount = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

// Digits and one decimal point, at most two decimal places.
const sanitizeAmountInput = (raw: string): string => {
  const [whole, ...rest] = raw.replace(/[^\d.]/g, '').split('.');
  return rest.length > 0 ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
};

// Whole cents, never above what's available. Rounding first avoids float
// error (0.29 * 100 is 28.999…), which a plain floor would turn into $0.28.
const maxWithdrawAmount = (available: number): string => {
  const cents = Math.round(available * 100);
  const capped = cents / 100 > available ? cents - 1 : cents;
  return (Math.max(capped, 0) / 100).toFixed(2);
};

const amountError = (mode: TransferMode, raw: string, available: number | null): string | null => {
  const value = Number(raw);
  if (!raw.trim() || !Number.isFinite(value) || value <= 0) {
    return 'Enter an amount greater than $0.';
  }
  if (mode === 'withdraw' && available != null && value > available) {
    return `You can withdraw up to ${formatTransferAmount(available)}.`;
  }
  return null;
};

const TransferCashModal: React.FC<TransferCashModalProps> = ({
  target,
  onClose,
  onCompleted,
  ira,
  availableToWithdraw: liveAvailable,
  balanceStatus,
  onRetryBalance,
  bankChoices,
  onRetryBanks,
  theme = 'dark',
}) => {
  const titleId = useId();
  const openerRef = useRef<HTMLElement | null>(null);
  const [step, setStep] = useState<'amount' | 'review' | 'success'>('amount');
  const [amount, setAmount] = useState('');
  const [iraInput, setIraInput] = useState<IraTransferInput>(emptyIraInput);
  const [chosenPlaidId, setChosenPlaidId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // Bumped whenever the modal closes or opens for a new target, so a request
  // still in flight from an earlier transfer can't write into this one.
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    if (!target) {
      return;
    }
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setStep('amount');
    setAmount('');
    setIraInput(emptyIraInput());
    setChosenPlaidId(null);
    setError(null);
    setSubmitting(false);
    submittingRef.current = false;
  }, [target]);

  const close = () => {
    requestIdRef.current += 1;
    // Done, X, Escape and a click outside all refresh after a successful transfer.
    if (step === 'success' && target) {
      onCompleted(target.accountId);
    }
    onClose();
    openerRef.current?.focus();
  };

  if (!target) {
    return null;
  }

  const numericAmount = Number(amount);
  const available = liveAvailable !== undefined ? liveAvailable : target.availableToWithdraw;
  const balanceError =
    balanceStatus === 'loading'
      ? 'Checking your available balance. Try again in a moment.'
      : balanceStatus === 'failed'
        ? "Couldn't check your available balance. Retry before reviewing."
        : null;
  const title =
    step === 'review'
      ? target.mode === 'deposit'
        ? 'Review deposit'
        : 'Review withdrawal'
      : target.mode === 'deposit'
        ? `Deposit to ${target.accountName}`
        : `Withdraw from ${target.accountName}`;
  // An attach-all wallet picks among its approved banks, starting from the
  // chip's bank; any other account uses its one locked funding bank.
  const approvedChoices = bankChoices?.choices.filter((choice) => choice.approved) ?? [];
  const chosenBank = bankChoices
    ? (approvedChoices.find((choice) => choice.plaidId === chosenPlaidId) ??
      approvedChoices.find((choice) => choice.plaidId === target.fundingChip?.plaidId) ??
      approvedChoices[0])
    : undefined;
  const plaidId = bankChoices ? chosenBank?.plaidId : target.fundingChip?.plaidId;
  const bankLabel = bankChoices ? (chosenBank?.name ?? 'No approved bank') : target.bankLabel;
  const banksError = !bankChoices
    ? null
    : bankChoices.status === 'loading'
      ? 'Loading your banks. Try again in a moment.'
      : bankChoices.status === 'failed'
        ? "Couldn't load your banks. Retry before reviewing."
        : !chosenBank
          ? 'None of your banks is approved by Apex yet.'
          : null;
  const fromName = target.mode === 'deposit' ? bankLabel : target.accountName;
  const toName = target.mode === 'deposit' ? target.accountName : bankLabel;
  const bankIsFrom = target.mode === 'deposit';
  const accountParty = (
    <LockedParty
      label={bankIsFrom ? 'To' : 'From'}
      name={target.accountName}
      detail={target.accountTypeLabel}
      mark="wavvest"
      bankName={target.bankLabel}
    />
  );
  const bankParty = bankChoices ? (
    <BankChoiceList
      label={bankIsFrom ? 'From' : 'To'}
      state={bankChoices}
      chosenPlaidId={chosenBank?.plaidId}
      onChoose={(next) => {
        setChosenPlaidId(next);
        setError(null);
      }}
      onRetry={onRetryBanks}
    />
  ) : (
    <LockedParty
      label={bankIsFrom ? 'From' : 'To'}
      name={target.bankLabel}
      detail={target.bankDetail}
      mark="bank"
      bankName={target.bankLabel}
      bankLogo={target.bankLogo}
    />
  );

  const review = () => {
    const nextError =
      banksError ??
      balanceError ??
      amountError(target.mode, amount, available) ??
      (ira ? iraInputError(target.mode, ira, iraInput, Number(amount)) : null);
    setError(nextError);
    if (!nextError) {
      setStep('review');
    }
  };

  const confirm = async () => {
    const failureMessage =
      target.mode === 'deposit'
        ? "Couldn't submit your deposit. Try again."
        : "Couldn't submit your withdrawal. Try again.";
    if (submittingRef.current || !plaidId) {
      setError(failureMessage);
      return;
    }
    // The balance can update while the review step is open: check it again.
    const staleAmountError =
      banksError ?? balanceError ?? amountError(target.mode, amount, available);
    if (staleAmountError) {
      setError(staleAmountError);
      setStep('amount');
      return;
    }
    const requestId = requestIdRef.current;
    const { accountId } = target;
    const isCurrent = () => requestIdRef.current === requestId;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const request = {
        amount: numericAmount,
        account_id: accountId,
        plaid_id: plaidId,
      };
      const response =
        target.mode === 'deposit'
          ? await makeDeposits([{ ...request, ...(ira ? iraDepositFields(iraInput) : {}) }])
          : await makeWithdrawals([
              { ...request, ...(ira ? iraWithdrawFields(ira, iraInput) : {}) },
            ]);
      // The API answers 200 even when Apex rejects the transfer; rejected
      // requests come back in `failed`.
      if ((response?.failed?.length ?? 0) > 0) {
        console.warn('Transfer rejected', response.failed);
        if (isCurrent()) {
          setError(failureMessage);
        }
        return;
      }
      if (isCurrent()) {
        setStep('success');
      } else {
        // Closed before it finished: still refresh balances and transfers.
        onCompleted(accountId);
      }
    } catch (submitError) {
      console.error('Failed to submit transfer', submitError);
      if (isCurrent()) {
        setError(failureMessage);
      }
    } finally {
      if (isCurrent()) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  return (
    <Modal
      open
      footer={null}
      width={500}
      onCancel={close}
      destroyOnClose
      maskClosable
      keyboard
      // Portalled to <body>, outside the themed page, so it carries the theme itself.
      className={`transfer-cash-modal ${theme}-theme`}
      title={
        <h2 id={titleId} className="transfer-cash-title">
          {step === 'success' ? (target.mode === 'deposit' ? 'Deposit' : 'Withdrawal') : title}
        </h2>
      }
      styles={{ mask: { background: 'rgba(0, 0, 0, 0.55)' } }}
    >
      <div role="document">
        {step === 'amount' && (
          <div className="transfer-cash-step">
            <label className="transfer-cash-amount" htmlFor="transfer-cash-amount">
              <span className="transfer-cash-field-label">Amount</span>
              <span className={`transfer-cash-amount-box${error ? ' is-invalid' : ''}`}>
                <span className="transfer-cash-dollar">$</span>
                <input
                  id="transfer-cash-amount"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  aria-label="Amount"
                  onChange={(event) => {
                    setAmount(sanitizeAmountInput(event.target.value));
                    setError(null);
                  }}
                />
              </span>
            </label>
            {balanceStatus === 'loading' && (
              <p className="transfer-cash-note">Checking your available balance…</p>
            )}
            {balanceStatus === 'failed' && (
              <p className="transfer-cash-note">
                Couldn't check your available balance.{' '}
                {onRetryBalance && (
                  <button type="button" className="transfer-cash-link" onClick={onRetryBalance}>
                    Retry
                  </button>
                )}
              </p>
            )}
            {target.mode === 'withdraw' && balanceError == null && available != null && (
              <div className="transfer-cash-available">
                <span>Available to withdraw: {formatTransferAmount(available)}</span>
                <button
                  type="button"
                  onClick={() => setAmount(maxWithdrawAmount(available!))}
                >
                  Max
                </button>
              </div>
            )}
            {bankIsFrom ? bankParty : accountParty}
            {bankIsFrom ? accountParty : bankParty}
            {ira && (
              <IraFields
                mode={target.mode}
                rules={ira}
                input={iraInput}
                onChange={(next) => {
                  setIraInput(next);
                  setError(null);
                }}
              />
            )}
            {error && (
              <p className="transfer-cash-error" role="alert">
                {error}
              </p>
            )}
            <div className="transfer-cash-actions">
              <Button onClick={close}>Cancel</Button>
              <Button
                type="primary"
                onClick={review}
                // Nothing to choose until Apex approves a bank (board TransferBank).
                disabled={bankChoices?.status === 'ready' && !chosenBank}
              >
                {target.mode === 'deposit' ? 'Review deposit' : 'Review withdrawal'}
              </Button>
            </div>
          </div>
        )}
        {step === 'review' && (
          <div className="transfer-cash-step">
            <div className="transfer-cash-review-hero">
              <span>{target.mode === 'deposit' ? "You're depositing" : "You're withdrawing"}</span>
              <strong>{formatTransferAmount(numericAmount)}</strong>
            </div>
            <dl className="transfer-cash-summary">
              <div>
                <dt>From</dt>
                <dd>{fromName}</dd>
              </div>
              <div>
                <dt>To</dt>
                <dd>{toName}</dd>
              </div>
              {ira && <IraReviewRows mode={target.mode} rules={ira} input={iraInput} />}
              <div>
                {/* TODO: no estimated-arrival field on the deposit/withdraw API. */}
                <dt>Estimated arrival</dt>
                <dd>—</dd>
              </div>
            </dl>
            {error && (
              <p className="transfer-cash-error" role="alert">
                {error}
              </p>
            )}
            <div className="transfer-cash-actions is-split">
              <Button onClick={() => setStep('amount')} disabled={submitting}>
                Back
              </Button>
              <Button type="primary" loading={submitting} onClick={() => void confirm()}>
                {target.mode === 'deposit' ? 'Confirm deposit' : 'Confirm withdrawal'}
              </Button>
            </div>
          </div>
        )}
        {step === 'success' && (
          <div className="transfer-cash-success">
            <span className="transfer-cash-success-mark" aria-hidden>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l5 5 9-10" />
              </svg>
            </span>
            <h2>
              {target.mode === 'deposit' ? 'Deposit submitted' : 'Withdrawal submitted'}
            </h2>
            <p>
              {formatTransferAmount(numericAmount)} from {fromName} to {toName}. You'll see it in Recent
              transfers.
            </p>
            <Button
              type="primary"
              onClick={close}
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

const IraFields = ({
  mode,
  rules,
  input,
  onChange,
}: {
  mode: TransferMode;
  rules: IraTransferRules;
  input: IraTransferInput;
  onChange: (next: IraTransferInput) => void;
}) => {
  if (rules.loading) {
    return <p className="transfer-cash-note">Loading IRA options…</p>;
  }
  const types = mode === 'deposit' ? contributionTypes(rules) : distributionTypes(rules);
  if (types.length === 0) {
    return (
      <p className="transfer-cash-error" role="alert">
        Couldn't load IRA options for this account. Close and try again.
      </p>
    );
  }
  if (mode === 'deposit') {
    const years = contributionYears(rules, input.contributionType);
    return (
      <div className="transfer-cash-ira-grid">
        <label className="transfer-cash-select">
          <span className="transfer-cash-field-label">Contribution year</span>
          <select
            aria-label="Contribution year"
            value={input.contributionYear ?? ''}
            onChange={(event) =>
              onChange({
                ...input,
                contributionYear: event.target.value ? Number(event.target.value) : undefined,
              })
            }
          >
            <option value="">Select</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </label>
        <label className="transfer-cash-select">
          <span className="transfer-cash-field-label">Contribution type</span>
          <select
            aria-label="Contribution type"
            value={input.contributionType ?? ''}
            onChange={(event) => {
              const contributionType = (event.target.value ||
                undefined) as IraTransferInput['contributionType'];
              const allowed = contributionYears(rules, contributionType);
              onChange({
                ...input,
                contributionType,
                // Keep the year only if the new type still allows it.
                contributionYear:
                  input.contributionYear && allowed.includes(input.contributionYear)
                    ? input.contributionYear
                    : allowed[0],
              });
            }}
          >
            <option value="">Select</option>
            {contributionTypes(rules).map((type) => (
              <option key={type} value={type}>
                {iraTypeLabel(type)}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  }
  const withholding = withholdingOptions(rules, input.distributionType);
  return (
    <div className="transfer-cash-ira">
      <label className="transfer-cash-select">
        <span className="transfer-cash-field-label">Distribution type</span>
        <select
          aria-label="Distribution type"
          value={input.distributionType ?? ''}
          onChange={(event) =>
            onChange({
              ...input,
              distributionType: (event.target.value ||
                undefined) as IraTransferInput['distributionType'],
            })
          }
        >
          <option value="">Select</option>
          {distributionTypes(rules).map((type) => (
            <option key={type} value={type}>
              {iraTypeLabel(type)}
            </option>
          ))}
        </select>
      </label>
      {(withholding.federal || withholding.state) && (
        <div className="transfer-cash-ira-grid">
          {withholding.federal && (
            <WithholdingInput
              label="Federal withholding"
              value={input.federalWithholding}
              onChange={(federalWithholding) => onChange({ ...input, federalWithholding })}
            />
          )}
          {withholding.state && (
            <WithholdingInput
              label="State withholding"
              value={input.stateWithholding}
              onChange={(stateWithholding) => onChange({ ...input, stateWithholding })}
            />
          )}
        </div>
      )}
      {withholding.state && (
        <label className="transfer-cash-check">
          <input
            type="checkbox"
            checked={input.stateWaiver}
            onChange={(event) => onChange({ ...input, stateWaiver: event.target.checked })}
          />
          <span>This distribution has a state withholding waiver</span>
        </label>
      )}
    </div>
  );
};

const WithholdingInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <label className="transfer-cash-select">
    <span className="transfer-cash-field-label">{label}</span>
    <span className="transfer-cash-money">
      <span aria-hidden>$</span>
      <input
        aria-label={label}
        inputMode="decimal"
        placeholder="0.00"
        value={value}
        onChange={(event) => onChange(sanitizeAmountInput(event.target.value))}
      />
    </span>
  </label>
);

const IraReviewRows = ({
  mode,
  rules,
  input,
}: {
  mode: TransferMode;
  rules: IraTransferRules;
  input: IraTransferInput;
}) => {
  if (mode === 'deposit') {
    return input.contributionType && input.contributionYear ? (
      <div>
        <dt>For</dt>
        <dd>
          {input.contributionYear} contribution · {iraTypeLabel(input.contributionType)}
        </dd>
      </div>
    ) : null;
  }
  if (!input.distributionType) {
    return null;
  }
  const withholding = withholdingOptions(rules, input.distributionType);
  const dollars = (raw: string) => formatTransferAmount(Number(raw) || 0);
  return (
    <>
      <div>
        <dt>Distribution type</dt>
        <dd>{iraTypeLabel(input.distributionType)}</dd>
      </div>
      {withholding.federal && (
        <div>
          <dt>Federal withholding</dt>
          <dd>{dollars(input.federalWithholding)}</dd>
        </div>
      )}
      {withholding.state && (
        <div>
          <dt>State withholding</dt>
          <dd>{dollars(input.stateWithholding)}</dd>
        </div>
      )}
      {withholding.state && input.stateWaiver && (
        <div>
          <dt>State waiver</dt>
          <dd>Yes</dd>
        </div>
      )}
    </>
  );
};

const PartyMark = ({
  mark,
  bankName,
  bankLogo,
}: {
  mark: 'bank' | 'wavvest';
  bankName: string;
  bankLogo?: string | null;
}) => {
  if (mark === 'wavvest') {
    return (
      <span className="transfer-cash-party-mark is-wavvest" aria-hidden>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M3 10c3-3 5-3 9 0s6 3 9 0" />
          <path d="M3 15c3-3 5-3 9 0s6 3 9 0" />
        </svg>
      </span>
    );
  }
  const image = fundingBankLogoSrc(bankLogo);
  if (image) {
    return <img className="transfer-cash-party-mark" src={image} alt="" />;
  }
  return (
    <span className="transfer-cash-party-mark is-bank" aria-hidden>
      {fundingBankInitials(bankName)}
    </span>
  );
};

const BankChoiceList = ({
  label,
  state,
  chosenPlaidId,
  onChoose,
  onRetry,
}: {
  label: string;
  state: TransferBankChoices;
  chosenPlaidId?: string;
  onChoose: (plaidId: string) => void;
  onRetry?: () => void;
}) => {
  const name = useId();
  const hasApproved = state.choices.some((choice) => choice.approved);
  return (
    <fieldset className="transfer-cash-banks">
      <legend className="transfer-cash-field-label">{label}</legend>
      {state.status === 'loading' && <p className="transfer-cash-note">Loading your banks…</p>}
      {state.status === 'failed' && (
        <p className="transfer-cash-note">
          Couldn't load your banks.{' '}
          {onRetry && (
            <button type="button" className="transfer-cash-link" onClick={onRetry}>
              Retry
            </button>
          )}
        </p>
      )}
      {state.status === 'ready' &&
        state.choices.map((choice) => (
          <label
            key={choice.plaidId}
            className={`transfer-cash-bank${choice.plaidId === chosenPlaidId ? ' is-chosen' : ''}${
              choice.approved ? '' : ' is-disabled'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={choice.plaidId}
              checked={choice.plaidId === chosenPlaidId}
              disabled={!choice.approved}
              onChange={() => onChoose(choice.plaidId)}
            />
            <PartyMark mark="bank" bankName={choice.name} bankLogo={choice.logo} />
            <span className="transfer-cash-party-copy">
              <strong>{choice.name}</strong>
              <span>{choice.detail}</span>
            </span>
            {!choice.approved && <span className="transfer-cash-chip">Pending</span>}
          </label>
        ))}
      {state.status === 'ready' && !hasApproved && (
        <p className="transfer-cash-warn" role="status">
          {state.choices.length > 0
            ? 'None of your banks is approved by Apex yet. Transfers open once one is approved, usually within one business day.'
            : 'No bank is linked to this account yet.'}
        </p>
      )}
    </fieldset>
  );
};

const LockedParty = ({
  label,
  name,
  detail,
  mark,
  bankName,
  bankLogo,
}: {
  label: string;
  name: string;
  detail: string;
  mark: 'bank' | 'wavvest';
  bankName: string;
  bankLogo?: string | null;
}) => (
  <div className="transfer-cash-party-block">
    <span className="transfer-cash-field-label">{label}</span>
    <div className="transfer-cash-party">
      <PartyMark mark={mark} bankName={bankName} bankLogo={bankLogo} />
      <div className="transfer-cash-party-copy">
        <strong>{name}</strong>
        <span>{detail}</span>
      </div>
      <LockOutlined aria-hidden />
    </div>
  </div>
);

export default TransferCashModal;
